import { type FC } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useStore } from "react-redux";
import { selectVisibleTabularViewColumns } from "~/features/search/redux/searchSelectors";
import { updateColumnVisibilityThunk } from "~/features/search/components/ProductResults/views/NewTabularView/ConfigureColumnsButton/actions";
import { ConfigureColumnsPopover } from "./ConfigureColumnsPopover";
import type { ColumnData } from "./ColumnConfigurator";
import type { RootState } from "~/app/store";

export const ConfigureColumnsButton: FC<{ className?: string }> = ({ className }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const store = useStore();

  // Build the column list from the Redux store each time the pop‑over opens
  const getColumns = () => {
    const state = store.getState() as RootState;
    const cols = selectVisibleTabularViewColumns(state) ?? [];
    return cols.map((c) => ({
      code: c.techValueUID,
      visible: c.visible,
      label: c.hoverTitle,
      originalPosition: c.originalPosition,
    }));
  };

  const handleSave = (draft: ColumnData[]) => {
    dispatch(updateColumnVisibilityThunk(draft));
  };

  const handleReset = (draft: ColumnData[]) => {
    const reset = draft.map((i) => ({ ...i, visible: true }));
    reset.sort((a, b) => {
      const posA = a.originalPosition ?? 0;
      const posB = b.originalPosition ?? 0;
      return posA - posB;
    });
    dispatch(updateColumnVisibilityThunk(reset, true));
    return reset;
  };

  return (
    <ConfigureColumnsPopover
      buttonLabel={t("page.srp.tabular.btn.manageColumns")}
      initialColumns={getColumns()}
      onSave={handleSave}
      onReset={handleReset}
      // className is passed only to the button – we expose it via a prop wrapper
      // (the generic pop‑over can accept an optional `buttonClassName` if needed)
    />
  );
};
